import {useDispatch} from 'react-redux'
import {useState,useRef,useEffect,useCallback} from 'react'
import { Line,Scatter } from 'react-chartjs-2'
import HowTo from './HowTo'
import Webcam from "react-webcam"
import useCountDown from "react-countdown-hook"
import {capitalize} from '../../util/util'
import useCurrentUser from '../../hooks/useCurrentUser'
import useWindowSize from '../../hooks/useWindowSize'
import * as poseDetection from '@tensorflow-models/pose-detection'
import * as tf from '@tensorflow/tfjs-core'
// Register one of the TF.js backends.
import '@tensorflow/tfjs-backend-webgl'
import {
	isWebview,
	torsoSideAngle,
	sideShoulderAngle,
	measureShoulderDistance,
	calculateArmLength,
	calculateForearmLength,
	calculateUpperArmLength,
	frontOverheadShoulderAngle,
	sideKneeAngle,
	confidentSide,
	normalizeX,
	repConsistency,
	tempoConsistency,
	depthScore,
	extractRepSlices,
	nonMonotonicIndexes,
	argMin,
	argMax,
	getKneeCaveTimeMap,
	restConsistency,
	kneeArticulationConsistency,
	centerLineConsistency,
	repsCenterLineScore,
	chestCenter,
	hipCenter,
	useAnimationFrame,
	poseFemurLength,
	poseHeight,
	balancingSide,
	calculateCenterStatus,
	calculateShoulderTilt,
	calculateHipTilt,
	tiltScore,
	kneeHeightScore,
	calculateThighLength,
	calculateCalfLength,
	heelHipDistanceEstimate,
	nearest
} from './util'
import {
	removeLeaderboardRecord,
	editLeaderboardRecord,
	addLeaderboardRecord,
	setMetricCreator,
	setPoses,
	setShow
} from './slice'
import styles from './AIDemo.module.css'
import Guides from './Guides'
import {dateToYMD} from '../../util/datetime'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'
import Spinner from 'react-bootstrap/Spinner'
import Leaderboard from './Leaderboard'
import KeypointLogViewer from './KeypointLogViewer'
import Poses from './Poses'
import testData from './testShoulderMobilityData.json'

const SCORE_THRESHOLD = 0.3
//320 is the max that the modal can input is within a 320x320 box T.T
const MODEL_INPUT_SIZE = 512

const detectorConfig = {
	modelType: poseDetection.movenet.modelType.SINGLEPOSE_THUNDER,
	enableTracking: true,
	trackerType: poseDetection.TrackerType.BoundingBox,
	multiPoseMaxDimension:MODEL_INPUT_SIZE
}

const ResultsTable = ({results})=>{
	return (
		<Table striped bordered>
			<thead>
				<tr>
					<th>Metric</th>
					<th>Left Angle</th>
					<th>Right Angle</th>
					<th>Asymmetry</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Overhead Sagittal Angle</td>
					<td>{results.front.leftAngle.toFixed(1)}&#xb0;</td>
					<td>{results.front.rightAngle.toFixed(1)}&#xb0;</td>
					<td>
						{Math.abs(results.front.rightAngle - results.front.leftAngle).toFixed(1)}&#xb0;
						{
							results.front.rightAngle - results.front.leftAngle <= 0 
							? ' (left)'
							: ' (right)'
						}
					</td>
				</tr>
				<tr>
					<td>Overhead Flexion Angle</td>
					<td>{results.left.flexionAngle.toFixed(1)}&#xb0;</td>
					<td>{results.right.flexionAngle.toFixed(1)}&#xb0;</td>
					<td>
						{Math.abs(results.right.flexionAngle - results.left.flexionAngle).toFixed(1)}&#xb0;
						{results.right.flexionAngle > results.left.flexionAngle ? ' (right)' : ' (left)'}
					</td>
				</tr>
				<tr>
					<td>Trunk Angle (during flexion)</td>
					<td>{results.left.flexionTrunkAngle.toFixed(1)}&#xb0;</td>
					<td>{results.right.flexionTrunkAngle.toFixed(1)}&#xb0;</td>
					<td/>
				</tr>
				<tr>
					<td>Posterior Extension Angle</td>
					<td>{results.left.extensionAngle.toFixed(1)}&#xb0;</td>
					<td>{results.right.extensionAngle.toFixed(1)}&#xb0;</td>
					<td>
						{Math.abs(results.right.extensionAngle - results.left.extensionAngle).toFixed(1)}&#xb0;
						{results.right.extensionAngle > results.left.extensionAngle ? ' (right)' : ' (left)'}
					</td>
				</tr>
				<tr>
					<td>Trunk Angle (during extension)</td>
					<td>{results.left.extensionTrunkAngle.toFixed(1)}&#xb0;</td>
					<td>{results.right.extensionTrunkAngle.toFixed(1)}&#xb0;</td>
					<td/>
				</tr>
			</tbody>
		</Table>
	)
}

const ArmOverheadMovement = ()=>{
	const dispatch = useDispatch()
	const [windowWidth,windowHeight] = useWindowSize()

	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const ratio = 1//windowWidth/windowHeight

	const [detector,setDetector] = useState(null)
	const [showHowTo,setShowHowTo] = useState(false)
	const [showResults,setShowResults] = useState(false)
	const [showLeaderboard,setShowLeaderboard] = useState(false)
	const testVidRef = useRef(null)
	const testVidLoadedRef = useRef(false)

	const canvasRef = useRef(null)

	const leftPassiveDegreeSpanRef = useRef(null)
	const leftActiveDegreeSpanRef = useRef(null)

	const rightPassiveDegreeSpanRef = useRef(null)
	const rightActiveDegreeSpanRef = useRef(null)

	//stores results from squat analysis
	const emptyResults = ()=>({
		keypointLog:[],
		front:{
			started:0,
			leftAngle:0,
			rightAngle:0,
		},
		right:{
			flexionStarted:0,
			extensionStarted:0,
			extensionAngle:0,
			extensionTrunkAngle:0,
			flexionAngle:0,
			flexionTrunkAngle:0
		},
		left:{
			flexionStarted:0,
			extensionStarted:0,
			extensionAngle:0,
			extensionTrunkAngle:0,
			flexionAngle:0,
			flexionTrunkAngle:0
		}
	})

	const emptyGuides = ()=>({
		armLength:{
			left:0,
			right:0
		},
	})

	const [results,setResults] = useState(testData)//useState(emptyResults())//
	const resultsRef = useRef(emptyResults())

	//console.log(results)

	const posesRef = useRef([])
	const [showGuides,setShowGuides] = useState(false)
	const [showOverlay,setShowOverlay] = useState(false)
	const overlayRef = useRef(null)
	const guidesRef = useRef(emptyGuides())

	const [name,setName] = useState('')
	const [email,setEmail] = useState('')

	useEffect(async()=>{
		setDetector(
			await poseDetection.createDetector(
				poseDetection.SupportedModels.MoveNet,
				detectorConfig
			)
		)
		//clear poses
		dispatch(setPoses([]))
	},[])

	//reference to if we have initialized the squat guides
	const initializedRef = useRef(false)
	const trackResultsRef = useRef(false)
	const webcamRef = useRef(null)
	const canTakeMeasurementRef = useRef(false)

	const stateDisplayTextMap = {
		waiting:'Waiting',
		setup:'Get Ready',
		measure:'Measuring'
	}

	const initState = 'waiting'
	const stateRef = useRef(initState)
	const stateSpanRef = useRef()
	const isDelayingRef = useRef(false)
	const overlayTimeoutRef = useRef(null)

	const setTemporaryOverlay = (text,ttl)=>{
		if(overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current)
		overlayRef.current.innerText = text
		setShowOverlay(true)
		overlayTimeoutRef.current = setTimeout(()=>{
			setShowOverlay(false)
		},ttl)
	}

	//predicates & functions
	const isState = state => state === stateRef.current
	const isDelaying = () => isDelayingRef.current
	const setCanTakeMeasurement = (bool) => { 
		canTakeMeasurementRef.current = bool
	}

	const toggleDelayedMeasurement = (delay)=>{
		isDelayingRef.current = true
		setTimeout(()=>{
			isDelayingRef.current = false
			setCanTakeMeasurement(true)
		},delay)
	}

	const canTakeMeasurement = () => canTakeMeasurementRef.current

	const setState = state => stateRef.current = state
	const getState = () => stateRef.current
	const getArmLength = (side)=> guidesRef.current.armLength[side]

	const armsOut = keypoints => {
		const threshold = 20

		const leftShoulder = keypoints[5]
		const rightShoulder = keypoints[6]

		const leftElbow = keypoints[7]
		const rightElbow = keypoints[8]

		const leftWrist = keypoints[9]
		const rightWrist = keypoints[10] 

		const leftArmOut = (
			Math.abs(leftShoulder.y - leftElbow.y) <= threshold &&
			Math.abs(leftShoulder.y - leftWrist.y) <= threshold
		)

		const rightArmOut = (
			Math.abs(rightShoulder.y - rightElbow.y) <= threshold &&
			Math.abs(rightShoulder.y - rightWrist.y) <= threshold
		)

		return  leftArmOut && rightArmOut
	}

	const leftArmUp = keypoints => {
		const threshold = guidesRef.current.shoulderDistance/3
		const leftShoulder = keypoints[5]
		const leftElbow = keypoints[7]
		const leftWrist = keypoints[9]

		return (
			leftShoulder.y > leftElbow.y &&
			leftElbow.y > leftWrist.y &&
			Math.abs(leftShoulder.x - leftElbow.x) <= threshold &&
			Math.abs(leftWrist.x - leftElbow.x) <= threshold
		)
	}

	const leftArmBehindBack = keypoints => {
		const elbowThreshold = guidesRef.current.shoulderDistance/5
		const wristThreshold = guidesRef.current.shoulderDistance/4
		const leftElbow = keypoints[7]
		const leftWrist = keypoints[9]
		const leftShoulder = keypoints[5]
		return (
			leftWrist.x > leftShoulder.x + wristThreshold &&
			leftElbow.x > leftShoulder.x + elbowThreshold
		)
	}

	const rightArmBehindBack = keypoints => {
		const elbowThreshold = guidesRef.current.shoulderDistance/5
		const wristThreshold = guidesRef.current.shoulderDistance/4
		const rightElbow = keypoints[8]
		const rightWrist = keypoints[10]
		const rightHip = keypoints[12]

		return (
			rightWrist.x < rightHip.x - wristThreshold &&
			rightElbow.x < rightHip.x - elbowThreshold
		)
	}

	const rightArmUp = keypoints => {
		const threshold = guidesRef.current.shoulderDistance/3
		const rightShoulder = keypoints[6]
		const rightElbow = keypoints[8]
		const rightWrist = keypoints[10] 

		return (
			rightShoulder.y > rightElbow.y &&
			rightElbow.y > rightWrist.y &&
			Math.abs(rightShoulder.x - rightElbow.x) <= threshold &&
			Math.abs(rightWrist.x - rightElbow.x) <= threshold
		)
	}

	const armsUp = keypoints => leftArmUp(keypoints) && rightArmUp(keypoints)

	const hasArmLengths = ()=>(
		guidesRef.current.armLength.left &&
		guidesRef.current.armLength.right
	)

	const hasResults = (side)=>(
		side === 'front' 
		? (resultsRef.current[side].leftAngle && resultsRef.current[side].rightAngle)
		: (resultsRef.current[side].flexionAngle && resultsRef.current[side].backAngle)
	)

	const hasSideResult = (side,metric) => resultsRef.current[side][metric] !== 0

	const logResult = (side,result) => {
		resultsRef.current[side] = {
			...resultsRef.current[side],
			...result
		}
	}

	const armAtSide = (side,keypoints)=>{
		const isLeft = side === 'left'
		const threshold = guidesRef.current.shoulderDistance/5

		const shoulder = keypoints[isLeft?5:6]

		const elbow = keypoints[isLeft?7:8]

		const wrist = keypoints[isLeft?9:10]

		return  (
			Math.abs(shoulder.x - elbow.x) <= threshold &&
			Math.abs(shoulder.x - wrist.x) <= threshold &&
			wrist.y > elbow.y &&
			elbow.y > shoulder.y
		)
	}

	const armsAtSide = keypoints => (
		armAtSide('left',keypoints) && 
		armAtSide('right',keypoints)
	)

	const facingFront = (keypoints,threshold=0.5) => measureShoulderDistance(keypoints)/guidesRef.current.shoulderDistance >= threshold

	//Assessment Definition defines the rules/predicates for transitioning into a state
	//what functions should run within a state (eg effects/logging). This object will
	//be converted into something more easily consumed by the inferencer. 
	const assessmentDefinition = {
		startState:'init',
		movements:{
			init:{},
			//this is the init step that looks for the start/ready signal
			setup:{
				onEnter: ()=>{
					setTemporaryOverlay('Get Ready',1500)
				},
				transition: () => isState('init')
			},
			ready:{
				onEnter:()=>{
					setTemporaryOverlay('Arms At Side',1500)
				},
				transition:(keypoints)=>(
					//only transition into ready if current state is init state
					//and arms are out
					isState('setup') &&
					armsOut(keypoints)
				),
				onInference: (keypoints) => {
					if (!hasArmLengths()) {
						guidesRef.current.shoulderDistance = measureShoulderDistance(keypoints)
						guidesRef.current.armLength.left = calculateArmLength('left',keypoints)
						guidesRef.current.armLength.right = calculateArmLength('right',keypoints)
					}
				}
			},
			front:{
				//what to do when entering the state
				onEnter:()=>{
					setTemporaryOverlay('Lift Both Arms',1500)
				},
				//predicate that returns true if the front state should be transitioned into
				transition:(keypoints)=>(
					//only transition into front if current state is ready state
					//and arms are at side
					isState('ready') &&
					armsAtSide(keypoints)
				),
				//function to call during each inference. This can be logging, warnings etc
				onInference:(keypoints)=>{
					if (canTakeMeasurement()) {
						const leftAngle = frontOverheadShoulderAngle('left',keypoints)
						const rightAngle = frontOverheadShoulderAngle('right',keypoints)
						setCanTakeMeasurement(false)
						logResult('front',{leftAngle,rightAngle})
						setTemporaryOverlay('Arms At Side',1500)
					}
					if (
						armsUp(keypoints) && 
						!hasResults('front') && 
						!isDelaying()
					) {
						toggleDelayedMeasurement(1000)
					}
				}
			},
			turningRight:{
				onEnter:()=>{
					setTemporaryOverlay('Turn Right',1500)
				},
				transition:(keypoints)=>(
					// only transition to turning right when there are results for the 
					// front facing test the current state is the front facing test and 
					// arms are at side
					hasResults('front')&&
					isState('front')&&
					armsAtSide(keypoints)
				)
			},
			leftFlexionAngle:{
				//what to do when entering the state
				onEnter:()=>{
					const flexionStarted = Date.now()
					logResult('left',{flexionStarted})
					setTemporaryOverlay('Lift Both Arms',1500)
				},
				//predicate that returns true if the front state should be transitioned into
				transition: keypoints => (
					isState('turningRight') &&
					!facingFront(keypoints,0.2)
				),
				//function to call during each inference. This can be logging, warnings etc
				onInference: keypoints => {
					if (canTakeMeasurement()) {
						setCanTakeMeasurement(false)
						const flexionAngle = sideShoulderAngle('left',keypoints)
						const flexionTrunkAngle = torsoSideAngle(keypoints,'left') - 90
						const ended = Date.now()
						logResult('left',{flexionAngle,flexionTrunkAngle,ended})
						setTemporaryOverlay('Arms At Side',1500)
					}
					if (
						leftArmUp(keypoints) && 
						!hasSideResult('left','flexionAngle') &&
						!isDelaying()
					) {
						toggleDelayedMeasurement(1000)
					}
				}
			},
			leftExtensionAngle:{
				//what to do when entering the state
				onEnter: ()=>{
					const extensionStarted = Date.now()
					logResult('left',{extensionStarted})
					setTemporaryOverlay('Extend Arms Back',1500)
				},
				//predicate that returns true if the front state should be transitioned into
				transition: keypoints => (
					isState('leftFlexionAngle') &&
					hasSideResult('left','flexionAngle') &&
					armAtSide('left',keypoints) &&
					!facingFront(keypoints,0.2)
				),
				//function to call during each inference. This can be logging, warnings etc
				onInference: keypoints => {
					if (canTakeMeasurement()) {
						setCanTakeMeasurement(false)
						const extensionAngle = sideShoulderAngle('left',keypoints)*-1
						const extensionTrunkAngle = torsoSideAngle(keypoints,'left') - 90
						const ended = Date.now()
						logResult('left',{extensionTrunkAngle,extensionAngle,ended})
						setTemporaryOverlay('Arms At Side',1500)
					}
					if (
						leftArmBehindBack(keypoints) && 
						!hasSideResult('left','extensionAngle')&&
						!isDelaying()
					) {
						toggleDelayedMeasurement(1000)
					}
				}
			},
			turningFront:{
				onEnter:()=>{
					setTemporaryOverlay('Face Camera',1500)
				},
				transition:(keypoints)=>(
					//only transition to turning right when there are results for the 
					//front facing test the current state is the front facing test
					//and arms are at side
					hasSideResult('left','extensionAngle') &&
					hasSideResult('left','flexionAngle') &&
					isState('leftExtensionAngle') &&
					armsAtSide(keypoints)
				)
			},
			turningLeft:{
				onEnter:()=>{
					setTemporaryOverlay('Turn Left',1500)
				},
				transition:(keypoints)=>(
					isState('turningFront') &&
					facingFront(keypoints,0.9)
				)
			},
			rightFlexionAngle:{
				//what to do when entering the state
				onEnter:()=>{
					const flexionStarted = Date.now()
					logResult('right',{flexionStarted})
					setTemporaryOverlay('Lift Both Arms',1500)
				},
				//predicate that returns true if the front state should be transitioned into
				transition: keypoints => (
					isState('turningLeft') &&
					!facingFront(keypoints,0.2)
				),
				//function to call during each inference. This can be logging, warnings etc
				onInference: keypoints => {
					if (canTakeMeasurement()) {
						setCanTakeMeasurement(false)
						const flexionAngle = sideShoulderAngle('right',keypoints)
						const flexionTrunkAngle = torsoSideAngle(keypoints,'right') - 90
						logResult('right',{flexionAngle,flexionTrunkAngle})
						setTemporaryOverlay('Arms At Side',1500)
					}
					if (
						rightArmUp(keypoints) && 
						!hasSideResult('right','flexionAngle')&&
						!isDelaying()
					) {
						toggleDelayedMeasurement(1000)
					}
				}
			},
			rightExtensionAngle:{
				//what to do when entering the state
				onEnter:()=>{
					const extensionStarted = Date.now()
					logResult('left',{extensionStarted})
					setTemporaryOverlay('Extend Arms Back',1500)
				},
				//predicate that returns true if the front state should be transitioned into
				transition: keypoints => (
					isState('rightFlexionAngle') &&
					hasSideResult('right','flexionAngle') &&
					armAtSide('right',keypoints) &&
					!facingFront(keypoints,0.2)
				),
				//function to call during each inference. This can be logging, warnings etc
				onInference: keypoints => {
					if (canTakeMeasurement()) {
						setCanTakeMeasurement(false)
						const extensionAngle = sideShoulderAngle('right',keypoints)*-1
						const extensionTrunkAngle = torsoSideAngle(keypoints,'right') - 90
						logResult('right',{extensionAngle,extensionTrunkAngle})
					}
					if (
						rightArmBehindBack(keypoints) && 
						!hasSideResult('right','extensionAngle')&&
						!isDelaying()
					) {
						toggleDelayedMeasurement(1000)
					}
				}
			},
			complete:{
				onEnter:()=>{
					setTemporaryOverlay('Done. Processing Results')
					setTimeout(()=>{
						cancelInferencing()
						console.log(resultsRef.current)
						setResults(resultsRef.current)
						setShowResults(true)
					},2000)
				},
				transition: ()=>(
					hasSideResult('right','extensionAngle')
				),
			}
		}//end movements
	}//end assessmentDefinition

	const runStateEnter = state => {
		const onEnter = assessmentDefinition.movements[state].onEnter || (()=>{})
		onEnter()
	}

	const transitionToState = state => {
		runStateEnter(state)
		setState(state)
		stateSpanRef.current.innerText = capitalize(state)
	}

	const runStateInference = (state,keypoints)=>{
		const onInference = assessmentDefinition.movements[state].onInference || (()=>{})
		onInference(keypoints)
	}

	//loop over assessment definition and extract all transition rules 
	const transitionRules = Object.entries(assessmentDefinition.movements)
	.map(([state,definition])=>(
		{
			state,
			transition:definition.transition || (()=>{})
		}
	))

	const infer = useCallback( async () => {
		if ((!webcamRef.current&&!testVidRef.current)||!detector) {
			//console.log('Waiting for dependencies')
		} else {
			const video = webcamRef.current?.video || testVidRef.current

			const ctx = canvasRef.current.getContext("2d")

			ctx.drawImage(video,0,0,MODEL_INPUT_SIZE,MODEL_INPUT_SIZE/ratio)

			if (!initializedRef.current||!trackResultsRef.current) {
				console.log('Not initialized')
				return
			}

			try {
				//console.log(`inferencing`)
				const poses = await detector.estimatePoses(canvasRef.current)
				
				dispatch(
					setPoses(
						JSON.parse(JSON.stringify(poses)).map(p=>(
							{
								...p,
								keypoints:p.keypoints.map(kpt=>({
									...kpt,
									x:MODEL_INPUT_SIZE - kpt.x
								}))
							}
						))//end stringify/parse deep copy hack
					)//end set poses
				)//end dispatch

				const {keypoints} = poses[0]
				const t = Date.now()
				resultsRef.current.keypointLog.push({keypoints,t})

				transitionRules.forEach(({transition,state})=>{
					if (transition(keypoints)) transitionToState(state)
				})

				runStateInference(getState(),keypoints)

				
			} catch(err) {
				console.error(err)
			}
		}
	},[detector,webcamRef,guidesRef])

	const {
		cancel:cancelInferencing,
		start:startInferencing
	} = useAnimationFrame(infer)

	const [started,setStarted] = useState(false)

	const startDemo = async ()=>{
		setStarted(true)
		cancelInferencing()
		setShowGuides(false)
		dispatch(setPoses([]))
		resultsRef.current = emptyResults()
		guidesRef.current = emptyGuides()
		stateSpanRef.current.innerText = stateDisplayTextMap['setup']

		initializedRef.current = true
		transitionToState(assessmentDefinition.startState)
		resultsRef.current = emptyResults()
		trackResultsRef.current = true
		if (testVidRef.current) testVidRef.current.play()
		startInferencing()
		//run a single inference to load model unto GPU
		const ctx = canvasRef.current.getContext("2d")
		if (testVidRef.current) {
			ctx.drawImage(testVidRef.current,0,0,MODEL_INPUT_SIZE,MODEL_INPUT_SIZE/ratio)
		} else {
			ctx.drawImage(webcamRef.current.video,0,0,MODEL_INPUT_SIZE,MODEL_INPUT_SIZE/ratio)
		}
		const poses = await detector.estimatePoses(canvasRef.current)
	}

	const restartDemo = ()=>{
		setShowResults(false)
		setShowGuides(false)
		cancelInferencing()
		dispatch(setPoses([]))
		setStarted(false)
		resultsRef.current = emptyResults()
	}

	const stopDemo = ()=>{
		setShowOverlay(false)
		setShowGuides(false)
		cancelInferencing()
		dispatch(setPoses([]))
		setStarted(false)
		resultsRef.current = emptyResults()
		stateSpanRef.current.innerText = stateDisplayTextMap[initState]
		if (testVidRef.current) {
			testVidRef.current.pause()
			testVidRef.current.currentTime = 0
		}
	}

	const handleStartTracking = ()=>{
		resultsRef.current = emptyResults()
		trackResultsRef.current = true
		startInferencing()
	}

	const handleReset = ()=>{
		cancelInferencing()
	}

	const [saving,setSaving] = useState(false)

	const handleSave = ()=>{
		setSaving(true)
		dispatch(addLeaderboardRecord({
			contact_email:email,
			rep_pace:repConsistency(results.reps),
			rest_pace:restConsistency(results.reps),
			depth_score:depthScore(results.reps,results.chestPosition,guidesRef.current.bottomY,guidesRef.current.topY),
			center_balance:centerLineConsistency(results.chestPosition),
			left_knee_balance:kneeArticulationConsistency(
				results.reps,
				results.leftKneePosition,
				results.kneeCaveMap.left
			),
			right_knee_balance:kneeArticulationConsistency(
				results.reps,
				results.rightKneePosition,
				results.kneeCaveMap.right
			),
			name:name,
		})).then(()=>{
			setSaving(false)
			setName('')
			setEmail('')
		})
	}

	const handleAddToProgress = ()=>{
		const metrics = [
			//overhead metrics
			{
				name:'Left Arm Overhead Sagittal Angle',
				description:'',
				//degrees
				unit_id:10,
				value:results.front.leftAngle.toFixed(1)
			},{
				name:'Right Arm Overhead Sagittal Angle',
				description:'',
				//degrees
				unit_id:10,
				value:results.front.rightAngle.toFixed(1)
			},{
				name:'Overhead Sagittal Angle Asymmetry',
				description:'',
				//degrees
				unit_id:10,
				value:(results.front.rightAngle - results.front.leftAngle).toFixed(1)
			},
			//left side metrics
			{
				name:'Left Arm Overhead Flexion Angle',
				description:'',
				//degrees
				unit_id:10,
				value:(results.left.flexionAngle).toFixed(1)
			},{
				name:'Left Side Trunk Angle During Flexion',
				description:'',
				//degrees
				unit_id:10,
				value:(results.left.flexionTrunkAngle).toFixed(1)
			},{
				name:'Left Arm Posterior Extension Angle',
				description:'',
				//degrees
				unit_id:10,
				value:(results.left.extensionAngle).toFixed(1)
			},{
				name:'Left Side Trunk Angle During Extension',
				description:'',
				//degrees
				unit_id:10,
				value:(results.left.extensionTrunkAngle).toFixed(1)
			},
			//right side metrics
			{
				name:'Right Arm Overhead Flexion Angle',
				description:'',
				//degrees
				unit_id:10,
				value:(results.right.flexionAngle).toFixed(1)
			},{
				name:'Right Side Trunk Angle During Flexion',
				description:'',
				//degrees
				unit_id:10,
				value:(results.right.flexionTrunkAngle).toFixed(1)
			},{
				name:'Right Arm Posterior Extension Angle',
				description:'',
				//degrees
				unit_id:10,
				value:(results.right.extensionAngle).toFixed(1)
			},{
				name:'Right Side Trunk Angle During Extension',
				description:'',
				//degrees
				unit_id:10,
				value:(results.right.extensionTrunkAngle).toFixed(1)
			},
			//Side asymmetries 
			{
				name:'Overhead Flexion Asymmetry',
				description:'',
				//degrees
				unit_id:10,
				value:(results.right.flexionAngle - results.left.flexionAngle).toFixed(1)
			},{
				name:'Posterior Extension Asymmetry',
				description:'',
				//degrees
				unit_id:10,
				value:(results.right.extensionAngle - results.left.extensionAngle).toFixed(1)
			},
		]

		//flatten log to form expected by api
		const aiLog = results.keypointLog.map(
			({keypoints}) => {
				const map = {}
				keypoints.forEach(({name,x,y})=>{
					map[`${name}_x`] = x
					map[`${name}_y`] = y
				})
				return map
			}
		)

		dispatch(setMetricCreator({
			show:true,
			category:'AI Overhead Arm Mobility',
			date:dateToYMD(new Date()),
			aiLog:JSON.stringify(results),
			metrics
		}))
	}

	const averageScore = (side)=>{
		const scores = [
			repConsistency(results[side].reps),
			depthScore(
				results[side].reps,
				results[side].chestPosition,
				guidesRef.current.squatBottomY,
				guidesRef.current.topY
			),
			restConsistency(results[side].reps),
			centerLineConsistency(results[side].chestPosition),
			kneeArticulationConsistency(
				results[side].reps,
				results[side].leftKneePosition,
				results[side].kneeCaveMap[side]
			),
			tiltScore(
				results[side].hipTilt.map(p=>({
					x:p.t - results[side].hipTilt[0].t,
					y:p.angle,
					status:p.status
				}))
			)
		]
		const average = scores.reduce(
			(carry,score)=>carry+score,
			0
		)/scores.length
		return average
	}

	return (
		<div className={styles.AIDemoWidget}>
			<div ref={overlayRef} style={{
				position:'absolute',
				width:'100vw',
				height:'100vh',
				background:'rgba(0,0,0,0.4)',
				color:'rgb(50,255,50)',
				display:showOverlay?'flex':'none',
				justifyContent:'center',
				fontWeight:600,
				alignItems:'center',
				fontSize:'110px',
				textAlign:'center',
				zIndex:3
			}}/>
			{!showResults&&
				<div className="m-auto">
					<div style={{position:'relative'}}>
						
						<svg style={{
							position:'absolute',
							top:0,left:0,
							zIndex:2,
							overflow:'visible',
							height:`${Math.min(windowWidth,windowHeight)}px`,
							width:`${Math.min(windowWidth,windowHeight)}px`,
						}}>
							{Boolean(webcamRef.current?.video)&&
								<>
									<Poses 
										scale={webcamRef.current.video.getBoundingClientRect().width/MODEL_INPUT_SIZE} 
										threshold={SCORE_THRESHOLD}
									/>
								</>
							}
						</svg>
					</div>
					
					<Webcam 
						style={{
							height:windowWidth>windowHeight?'100vh':'auto',
							width:windowWidth>=windowHeight?'auto':'100vw',
							display:'block',
							margin:'auto'
						}}
						ref={webcamRef}
						mirrored
						videoConstraints={{
							width: 720,
							height:720,
							facingMode:"user",
							deviceId:"c141e16a643707300abde9eaae8a522f531793da15be147c3e1d7f40dcdfaea7"
						}}
					/>
					<canvas 
						style={{display:'none'}} 
						width={MODEL_INPUT_SIZE} height={MODEL_INPUT_SIZE} 
						ref={canvasRef}
					/>
					<div style={{
						position:'absolute',
						top:'4px',
						right:'4px',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						borderRadius:'8px',
						zIndex:3,
						width:'200px'
					}}>
						<div className="d-flex align-items-stretch justify-content-around mb-2" style={{fontSize:'16px'}}>
							<div>
								<small 
									style={{fontSize:'14px',margin:0}} 
									className="text-muted d-block text-center"
								>
									Left
								</small>
							</div>
							<span style={{background:'#ccc',width:'1px'}}/>
							<div>
								<small 
									style={{fontSize:'14px',margin:0}} 
									className="text-muted d-block text-center"
								>
									Right
								</small>
							</div>
						</div>
						<div className="d-flex justify-content-between">
							<small className="mr-2" ref={stateSpanRef}>Waiting</small>
							<small className={Boolean(detector)?'text-success':'text-muted'}>
								{Boolean(detector)?'AI Downloaded':'Downloading AI...'}
							</small>
						</div>
					</div>
					<div style={{
						position:'absolute',
						bottom:0,
						left:0,
						display:'flex',
						flexWrap:'wrap',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						zIndex:3
					}}>
						<Button
							className="mr-2"
							variant="outline-primary"
							onClick={()=>{
								setShowHowTo(p=>!p)
							}}
						>
							<i className="fa fa-question-circle" /> How To
						</Button>
						{!started&&
							<Button className="mr-2" onClick={startDemo} variant="outline-success">
								Start
							</Button>
						}
						{started&&
							<Button className="mr-2" onClick={stopDemo} variant="outline-danger">
								Stop
							</Button>
						}
						{Boolean(testVidRef.current)&&
							<Button className="mr-2" onClick={()=>{
								testVidRef.current.pause()
							}} variant="outline-info">
								Pause
							</Button>
						}
						{!isWebview()&&
							<Button 
								className="mr-2"
								onClick={()=>{
									dispatch(setPoses([]))
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{showResults&&
				<div style={{
					width:'100vw',
					height:'100vh',
					overflow:'auto',
					padding:'16px'
				}}>
					<div style={{
						fontSize:'32px',
						textAlign:'center',
						marginTop:'44px',
						marginBottom:'16px'
					}}>
						Results Analysis
					</div>
					<div>
						<ResultsTable 
							results={results} 
						/>
					</div>
					<KeypointLogViewer 
						id="arm-mobility" 
						demo="arm-overhead-movement"
						results={results} 
						size={512}
					/>
					<Button 
						onClick={handleAddToProgress}
						variant="outline-success" 
						className="my-3"
						block
					>
						Add to {isClient?'':"Client's"} Progress Metrics
					</Button>
					<div className="mt-5 d-flex">
						<Button 
							className="mr-2"
							onClick={restartDemo}
							variant="outline-dark"
						>
							Restart
						</Button>
						{!isWebview()&&
							<Button 
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
					</div>
				</div>
			}
			{(parseInt(user.user_id_number) === 100017099 || window.location.hostname === 'localhost')&&
				<div style={{
					position:'absolute',
					top:0,left:0,
					transform:`translateX(${showLeaderboard?'0':'-100%'})`, 
					transition:'0.2s',
					width:'600px',
					maxWidth:'100%',
					height:'100vh',
					zIndex:6,
					boxShadow:showLeaderboard?'0 0 16px rgba(0,0,0,0.6)':'none'
				}}>
					<button 
						onClick={()=>{
							setShowLeaderboard( p => !p )
						}}
						style={{
							position:'absolute',
							top:'80px',right:0,
							transform:'translateX(100%)',
							padding:'16px',
							background:'#fff',
							border:'none',
							zIndex:5,
							boxShadow:'0 0 16px rgba(0,0,0,0.2)',
						}}
					>
						Leaderboard
					</button>
					<div style={{
						width:'100%',
						height:'100%',
						overflow:'auto',
						position:'absolute',
						background:'#fff',
						padding:'8px',
						top:0,left:0,
						zIndex:6
					}}>
						<Leaderboard/>
					</div>
				</div>
			}
			{showHowTo&&
				<HowTo onHide={
					()=>{setShowHowTo(false)}
				}>
					<strong>What is this?</strong>
					<p>
						With only a webcam, our system will automatically measure your shoulder mobility
					</p>
					<strong>How do I use this?</strong>
					<ul>
						<li><span className="mr-2">1.</span>Press the green <span id={styles.StartHowTo}>Start</span> button & <strong>wait</strong> till it turns into a red <span id={styles.StopHowTo}>Stop</span> button (the first time always takes a few seconds)</li>
						<li><span className="mr-2">2.</span>Step back until full body is in view of camera.</li>
						<li><span className="mr-2">3.</span>When ready, face camera directly and hold both arms out to a "T."</li>
						<li><span className="mr-2">4.</span>Put arms at side.</li>
						<li><span className="mr-2">5.</span>Lift arms forward until straight overhead.</li>
					</ul>
					<div className="d-flex align-items-end flex-wrap justify-content-around">
						<a target="_blank" href="https://www.youtube.com/watch?v=rPjhkwmExEY">Check out a live demo</a>
						<div className="text-center">
							<div><small>For more more info read the</small></div>
							<a target="_blank" href="https://www.fitsw.com/blog/exercise-motion-analysis-ai/">Blog Post About fitsw.ai</a>
						</div>
					</div>
				</HowTo>
			}
		</div>
	)
}

export default ArmOverheadMovement